import type { ProductVariant } from "@/lib/api-client";

export interface VariantSelectorProps {
  variants: ProductVariant[];
  selectedVariantId: string;
  onSelect: (variantId: string) => void;
}

interface VariantAxis {
  code: string;
  label: string;
  values: Array<{ normalizedValue: string; label: string; hexColor: string | null }>;
}

function buildAxes(variants: ProductVariant[]): VariantAxis[] {
  const axes = new Map<string, VariantAxis>();
  for (const variant of variants) {
    for (const attr of variant.attributes) {
      let axis = axes.get(attr.code);
      if (!axis) {
        axis = { code: attr.code, label: attr.label, values: [] };
        axes.set(attr.code, axis);
      }
      if (!axis.values.some((v) => v.normalizedValue === attr.normalizedValue)) {
        axis.values.push({ normalizedValue: attr.normalizedValue, label: attr.value, hexColor: attr.hexColor });
      }
    }
  }
  return [...axes.values()];
}

function matchesSelection(variant: ProductVariant, selection: Record<string, string>): boolean {
  const codes = Object.keys(selection);
  if (variant.attributes.length !== codes.length) return false;
  return codes.every((code) => variant.attributes.some((a) => a.code === code && a.normalizedValue === selection[code]));
}

/**
 * Porté à l'identique depuis apps/web/components/product/VariantSelector.tsx
 * (aucune dépendance Next.js dans l'original).
 */
export function VariantSelector({ variants, selectedVariantId, onSelect }: VariantSelectorProps) {
  if (variants.length <= 1) return null;

  const axes = buildAxes(variants);
  const selectedVariant = variants.find((v) => v.id === selectedVariantId) ?? (variants[0] as ProductVariant);
  const selection: Record<string, string> = Object.fromEntries(
    selectedVariant.attributes.map((a) => [a.code, a.normalizedValue]),
  );

  function pickBestVariant(code: string, normalizedValue: string): ProductVariant | undefined {
    const nextSelection = { ...selection, [code]: normalizedValue };
    return (
      variants.find((v) => matchesSelection(v, nextSelection)) ??
      variants.find((v) => v.attributes.some((a) => a.code === code && a.normalizedValue === normalizedValue))
    );
  }

  function handlePick(code: string, normalizedValue: string) {
    const match = pickBestVariant(code, normalizedValue);
    if (match) onSelect(match.id);
  }

  return (
    <div className="space-y-3">
      {axes.map((axis) => (
        <div key={axis.code}>
          <p className="mb-1.5 font-body text-xs font-semibold uppercase tracking-wide text-navy">{axis.label}</p>
          <div className="flex flex-wrap gap-2">
            {axis.values.map((val) => {
              const active = selection[axis.code] === val.normalizedValue;
              const candidate = pickBestVariant(axis.code, val.normalizedValue);
              const outOfStock = candidate ? !candidate.stock.inStock : false;
              return (
                <button
                  key={val.normalizedValue}
                  type="button"
                  onClick={() => handlePick(axis.code, val.normalizedValue)}
                  aria-pressed={active}
                  title={outOfStock ? `${val.label} (rupture)` : val.label}
                  className={[
                    "flex items-center gap-1.5 rounded-sm border px-3 py-1.5 font-body text-sm",
                    active ? "border-navy bg-navy text-cream" : "border-line bg-white text-ink hover:border-navy",
                    outOfStock && !active ? "text-muted line-through" : "",
                  ].join(" ")}
                >
                  {val.hexColor ? (
                    <span
                      aria-hidden="true"
                      className="h-4 w-4 rounded-full border border-line"
                      style={{ backgroundColor: val.hexColor }}
                    />
                  ) : null}
                  {val.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
